/**
 * ManualResultSelector — Non-premium validation workflow
 *
 * When AI is not accessible, the operator manually selects
 * POSITIF / NÉGATIF / DOUTEUX for each blot.
 * The DOUTEUX criteria surface directly from the immunoblot config guide.
 */
import {
  Box,
  VStack,
  HStack,
  Text,
  Icon,
  Badge,
  Tooltip,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  Divider,
} from '@chakra-ui/react'
import { LuCircleCheck, LuCircleX, LuCircleAlert, LuBookOpen } from 'react-icons/lu'
import { useTranslation } from 'react-i18next'

export type ManualResult = 'POSITIF' | 'NÉGATIF' | 'DOUTEUX' | null

interface InterpretationCriteria {
  type: string
  description: string
  bandId?: string
  value?: number
}

interface ImmunoblotInterpretation {
  positive: { criteria: InterpretationCriteria[]; result: string; conclusion: string }
  negative: { criteria: InterpretationCriteria[]; result: string; conclusion: string }
  equivocal?: { criteria: InterpretationCriteria[]; result: string; conclusion: string }
}

interface BandConfig {
  id: string
  name: string
  molecularWeight: number
  required?: boolean
}

interface ManualResultSelectorProps {
  blotId?: number | string
  value: ManualResult
  onChange: (result: ManualResult) => void
  interpretation?: ImmunoblotInterpretation | null
  testName?: string
  isDisabled?: boolean
  /** Set of band names placed by the user on the image */
  detectedBandNames?: Set<string>
  /** Full band config list (for colored reference panel) */
  bands?: BandConfig[]
}

const RESULTS: {
  key: ManualResult
  icon: typeof LuCircleCheck
  color: string
  activeBg: string
  activeText: string
  borderColor: string
  hoverBg: string
  label: string
  hotkey: string
}[] = [
  {
    key: 'POSITIF',
    icon: LuCircleCheck,
    color: 'green',
    activeBg: '#22c55e',
    activeText: 'white',
    borderColor: '#16a34a',
    hoverBg: '#f0fdf4',
    label: 'POSITIF',
    hotkey: 'P',
  },
  {
    key: 'DOUTEUX',
    icon: LuCircleAlert,
    color: 'orange',
    activeBg: '#f97316',
    activeText: 'white',
    borderColor: '#ea580c',
    hoverBg: '#fff7ed',
    label: 'ÉQUIVOQUE',
    hotkey: 'D',
  },
  {
    key: 'NÉGATIF',
    icon: LuCircleX,
    color: 'red',
    activeBg: '#ef4444',
    activeText: 'white',
    borderColor: '#dc2626',
    hoverBg: '#fef2f2',
    label: 'NÉGATIF',
    hotkey: 'N',
  },
]

interface ManualResultSelectorPropsWithHotkeys extends ManualResultSelectorProps {
  /** Show keyboard shortcut hints on each button */
  showHotkeys?: boolean
}

/** Returns { allowed, reason } for selecting POSITIF based on placed bands and test criteria. */
export function checkPositifAllowed(
  detected: Set<string>,
  interpretation: ImmunoblotInterpretation | null | undefined,
  bands: BandConfig[]
): { allowed: boolean; reason: string | null } {
  if (!interpretation) return { allowed: true, reason: null }
  if (detected.size === 0) return { allowed: false, reason: 'Aucune bande placée' }

  const criteria = interpretation.positive.criteria

  // required_band — e.g. P30 for Toxo
  const req = criteria.find((c: any) => c.type === 'required_band')
  if (req?.bandId && !detected.has(req.bandId)) {
    return { allowed: false, reason: `La bande ${req.bandId} est obligatoire` }
  }

  // minimum_bands
  const minC = criteria.find((c: any) => c.type === 'minimum_bands')
  if (minC?.value && detected.size < minC.value) {
    return { allowed: false, reason: `${detected.size}/${minC.value} bandes placées` }
  }

  // extreme_bands — Echino: any specific band is sufficient
  // (already covered by detected.size > 0 above)

  // major_bands — Schisto: P30-34 or P22-24 must be among placed bands
  const majorC = criteria.find((c: any) => c.type === 'major_bands')
  if (majorC) {
    const sorted = [...bands].sort((a, b) => b.molecularWeight - a.molecularWeight)
    const major = sorted.slice(0, 2).map(b => b.name)
    if (!major.some(name => detected.has(name))) {
      return { allowed: false, reason: `${major.join(' ou ')} requis` }
    }
  }

  return { allowed: true, reason: null }
}

export const ManualResultSelector: React.FC<ManualResultSelectorPropsWithHotkeys> = ({
  value,
  onChange,
  interpretation,
  testName,
  showHotkeys = false,
  isDisabled = false,
  detectedBandNames = new Set(),
  bands = [],
}) => {
  const { t } = useTranslation()
  const hasEquivocal = !!interpretation?.equivocal
  const positifCheck = checkPositifAllowed(detectedBandNames, interpretation, bands)

  return (
    <VStack spacing={3} align="stretch" w="full">
      {/* Test label */}
      {testName && (
        <HStack spacing={2}>
          <Text fontSize="xs" fontWeight="bold" color="gray.500" textTransform="uppercase" letterSpacing="wider">
            Validation
          </Text>
          <Badge colorScheme="purple" variant="subtle" fontSize="2xs" borderRadius="full" px={2}>
            {testName}
          </Badge>
        </HStack>
      )}

      {/* Result buttons — DOUTEUX only shown if the test has an equivocal interpretation */}
      <VStack spacing={2} w="full">
        {RESULTS.filter(r => r.key !== 'DOUTEUX' || hasEquivocal).map(({ key, icon, activeBg, activeText, borderColor, hoverBg, label, hotkey }) => {
          const isActive = value === key
          // POSITIF is blocked until enough bands are placed per the test criteria
          const isPositifBlocked = key === 'POSITIF' && !positifCheck.allowed
          const buttonDisabled = isDisabled || isPositifBlocked
          return (
            <Tooltip
              key={key}
              label={isPositifBlocked ? positifCheck.reason ?? 'Placez les bandes requises' : undefined}
              isDisabled={!isPositifBlocked}
              hasArrow
              placement="left"
            >
            <Box
              as="button"
              w="full"
              onClick={() => !buttonDisabled && onChange(isActive ? null : key)}
              disabled={buttonDisabled}
              py={3.5}
              px={4}
              borderRadius="xl"
              border="2px solid"
              borderColor={isActive ? borderColor : isPositifBlocked ? 'gray.100' : 'gray.200'}
              bg={isActive ? activeBg : 'white'}
              color={isActive ? activeText : isPositifBlocked ? 'gray.300' : 'gray.700'}
              cursor={buttonDisabled ? 'not-allowed' : 'pointer'}
              transition="all 0.15s ease"
              _hover={!buttonDisabled ? { bg: isActive ? activeBg : hoverBg, borderColor: borderColor, transform: 'translateY(-1px)', boxShadow: 'lg' } : {}}
              _active={!buttonDisabled ? { transform: 'translateY(0)' } : {}}
              display="flex"
              alignItems="center"
              gap={3}
              boxShadow={isActive ? 'lg' : 'sm'}
              opacity={buttonDisabled ? 0.35 : 1}
            >
              <Icon
                as={icon}
                boxSize={6}
                color={isActive ? activeText : borderColor}
                flexShrink={0}
              />
              <Text
                fontWeight="bold"
                fontSize="md"
                letterSpacing="wide"
                flex={1}
                textAlign="left"
              >
                {label}
              </Text>
              {/* Hotkey badge */}
              {showHotkeys && (
                <Box
                  as="kbd"
                  fontSize="xs"
                  fontFamily="mono"
                  bg={isActive ? 'whiteAlpha.300' : 'gray.100'}
                  color={isActive ? activeText : 'gray.500'}
                  px={1.5}
                  py={0.5}
                  borderRadius="md"
                  border="1px solid"
                  borderColor={isActive ? 'whiteAlpha.400' : 'gray.300'}
                  flexShrink={0}
                  lineHeight="1.4"
                >
                  {hotkey}
                </Box>
              )}
              {isActive && !showHotkeys && (
                <Box w={2} h={2} borderRadius="full" bg="whiteAlpha.700" flexShrink={0} />
              )}
            </Box>
            </Tooltip>
          )
        })}
      </VStack>

      {/* Keyboard shortcuts hint */}
      {showHotkeys && (
        <HStack spacing={3} justify="center" pt={1}>
          <HStack spacing={1}>
            <Box as="kbd" fontSize="2xs" fontFamily="mono" bg="gray.100" color="gray.500" px={1.5} py={0.5} borderRadius="sm" border="1px solid" borderColor="gray.300">←</Box>
            <Box as="kbd" fontSize="2xs" fontFamily="mono" bg="gray.100" color="gray.500" px={1.5} py={0.5} borderRadius="sm" border="1px solid" borderColor="gray.300">→</Box>
            <Text fontSize="2xs" color="gray.400">naviguer</Text>
          </HStack>
          <HStack spacing={1}>
            <Box as="kbd" fontSize="2xs" fontFamily="mono" bg="gray.100" color="gray.500" px={1.5} py={0.5} borderRadius="sm" border="1px solid" borderColor="gray.300">↵</Box>
            <Text fontSize="2xs" color="gray.400">suivant</Text>
          </HStack>
        </HStack>
      )}

      {/* Current selection feedback — subtle */}
      {value && interpretation && (
        <Box
          bg={value === 'POSITIF' ? 'green.50' : value === 'NÉGATIF' ? 'red.50' : 'orange.50'}
          borderRadius="lg"
          px={3}
          py={2}
          border="1px solid"
          borderColor={value === 'POSITIF' ? 'green.200' : value === 'NÉGATIF' ? 'red.200' : 'orange.200'}
        >
          <Text
            fontSize="xs"
            color={value === 'POSITIF' ? 'green.700' : value === 'NÉGATIF' ? 'red.700' : 'orange.700'}
            fontStyle="italic"
          >
            {value === 'POSITIF'
              ? interpretation.positive.conclusion
              : value === 'NÉGATIF'
              ? interpretation.negative.conclusion
              : interpretation.equivocal?.conclusion ?? ''}
          </Text>
        </Box>
      )}

      {/* Criteria accordion */}
      {interpretation && (
        <Accordion allowToggle>
          <AccordionItem
            border="1px solid"
            borderColor="gray.200"
            borderRadius="lg"
            overflow="hidden"
          >
            <AccordionButton bg="gray.50" _hover={{ bg: 'gray.100' }} py={2} px={3}>
              <HStack flex={1} spacing={2}>
                <Icon as={LuBookOpen} color="gray.400" boxSize={3.5} />
                <Text fontSize="xs" fontWeight="semibold" color="gray.500">
                  Critères du guide fabricant
                </Text>
              </HStack>
              <AccordionIcon color="gray.400" />
            </AccordionButton>
            <AccordionPanel pb={3} px={3} bg="white">
              <VStack align="stretch" spacing={2}>

                <HStack align="start" spacing={2}>
                  <Icon as={LuCircleCheck} color="green.500" boxSize={3.5} mt={0.5} flexShrink={0} />
                  <VStack align="start" spacing={0.5}>
                    <Text fontSize="xs" fontWeight="bold" color="green.700">POSITIF</Text>
                    {interpretation.positive.criteria.map((c, i) => (
                      <Text key={i} fontSize="xs" color="gray.600">• {c.description}</Text>
                    ))}
                  </VStack>
                </HStack>

                {hasEquivocal && interpretation.equivocal && (
                  <>
                    <Divider />
                    <HStack align="start" spacing={2}>
                      <Icon as={LuCircleAlert} color="orange.500" boxSize={3.5} mt={0.5} flexShrink={0} />
                      <VStack align="start" spacing={0.5}>
                        <Text fontSize="xs" fontWeight="bold" color="orange.700">ÉQUIVOQUE</Text>
                        {interpretation.equivocal.criteria.map((c, i) => (
                          <Text key={i} fontSize="xs" color="gray.600">• {c.description}</Text>
                        ))}
                      </VStack>
                    </HStack>
                  </>
                )}

                <Divider />
                <HStack align="start" spacing={2}>
                  <Icon as={LuCircleX} color="red.500" boxSize={3.5} mt={0.5} flexShrink={0} />
                  <VStack align="start" spacing={0.5}>
                    <Text fontSize="xs" fontWeight="bold" color="red.700">NÉGATIF</Text>
                    {interpretation.negative.criteria.map((c, i) => (
                      <Text key={i} fontSize="xs" color="gray.600">• {c.description}</Text>
                    ))}
                  </VStack>
                </HStack>

              </VStack>
            </AccordionPanel>
          </AccordionItem>
        </Accordion>
      )}
    </VStack>
  )
}

/**
 * Case-level result aggregator for the page-level summary badge.
 * POSITIF if any blot is positive, DOUTEUX if mixed, NÉGATIF if all negative.
 */
export const CaseResultSummary: React.FC<{
  blotResults: Record<number, ManualResult>
}> = ({ blotResults }) => {
  const results = Object.values(blotResults).filter(Boolean) as string[]
  if (results.length === 0) return null

  const hasPositif = results.includes('POSITIF')
  const hasDouteux = results.includes('DOUTEUX')
  const allNegatif = results.every((r) => r === 'NÉGATIF')

  const overall: ManualResult = hasPositif
    ? 'POSITIF'
    : hasDouteux
    ? 'DOUTEUX'
    : allNegatif
    ? 'NÉGATIF'
    : null

  if (!overall) return null

  const color = overall === 'POSITIF' ? 'green' : overall === 'DOUTEUX' ? 'orange' : 'red'
  const icon = overall === 'POSITIF' ? LuCircleCheck : overall === 'DOUTEUX' ? LuCircleAlert : LuCircleX

  return (
    <Badge
      colorScheme={color}
      px={3}
      py={1}
      borderRadius="full"
      fontSize="xs"
      display="flex"
      alignItems="center"
      gap={1.5}
    >
      <Icon as={icon} boxSize={3} />
      {overall}
    </Badge>
  )
}
